import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Megaphone } from 'lucide-react';
import { Avatar, usePartyUi } from '@gmp/shared-ui';

/** How long one announcer or AI-player line stays on screen. */
const SHOW_MS = 7000;

/**
 * The announcer ticker: the newest commentary or AI-player line, shown briefly at the top of the screen.
 * Nothing renders when the host has the announcer off (the feed stays empty).
 */
export function FeedTicker({ big = false }: { big?: boolean }) {
  const { room, now, member } = usePartyUi();
  const line = room.feed.at(-1);
  const [, rerender] = useState(0);
  useEffect(() => {
    if (!line) return;
    const left = line.at + SHOW_MS - now();
    if (left <= 0) return;
    const t = setTimeout(() => rerender((n) => n + 1), left + 50);
    return () => clearTimeout(t);
  }, [line, now]);
  const visible = line && now() - line.at < SHOW_MS ? line : null;
  const who = visible?.from ? member(visible.from) : undefined;
  return (
    <div className={`pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 ${big ? 'bottom-8' : 'top-2'}`} aria-live="polite">
      <AnimatePresence>
        {visible && (
          <motion.div
            key={visible.id}
            initial={{ y: big ? 40 : -40, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: big ? 40 : -40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24 }}
            className={`flex items-center gap-3 rounded-2xl bg-ink-2/95 shadow-xl ring-2 ring-[var(--accent)] ${big ? 'px-6 py-4 text-3xl max-w-5xl' : 'px-3 py-2 text-sm max-w-md'}`}
            data-testid="feed-line"
          >
            {who ? <Avatar id={who.avatar} size={big ? 56 : 28} color={who.color} /> : <Megaphone size={big ? 40 : 20} style={{ color: 'var(--accent)' }} aria-hidden />}
            <p className="font-bold leading-snug">
              <span className="text-mute">{who ? `${who.nickname}: ` : 'Announcer: '}</span>
              {visible.text}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
